import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ChevronUp, Upload } from 'lucide-react';

interface UploadItem {
  id: string;
  title: string;
  platforms: ('YouTube' | 'Instagram' | 'TikTok')[];
  status: 'uploading' | 'completed' | 'skipped' | 'failed' | 'queued';
  progress?: number;
  timeLeft?: string;
  estimatedTime?: string;
  startedAt?: number;
  publishTiming?: 'now' | 'schedule';
  scheduledAt?: string;
  cancelledPlatforms?: string[];
}

interface BackgroundUploaderProps {
  uploads: UploadItem[];
  onClose?: () => void;
  onRetry?: (uploadId: string) => void;
  onAdd?: () => void;
  onOpenUploadModal?: () => void;
  onSimulateFailure?: () => void;
  onSimulateCancellation?: () => void;
}

type TabType = 'all' | 'completed' | 'skipped' | 'failed';

function getCompletionLabel(upload: UploadItem) {
  if (upload.publishTiming !== 'schedule' || !upload.scheduledAt) {
    return 'Uploaded successfully';
  }

  const scheduledDate = new Date(upload.scheduledAt);
  if (Number.isNaN(scheduledDate.getTime())) return 'Scheduled successfully';

  return `Scheduled for ${scheduledDate.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function BackgroundUploader({
  uploads,
  onClose,
  onRetry,
  onAdd,
  onOpenUploadModal,
  onSimulateFailure,
  onSimulateCancellation,
}: BackgroundUploaderProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  // Filter uploads based on active tab
  const filteredUploads = uploads.filter(upload => {
    if (activeTab === 'all') return true;
    return upload.status === activeTab;
  });

  // Calculate active uploads count
  const activeCount = uploads.filter(u => u.status === 'uploading' || u.status === 'queued').length;
  const totalCount = uploads.length;
  const latestUpload = uploads[uploads.length - 1];
  const activeUpload = uploads.find(u => u.status === 'uploading');

  // Get platform badge color
  const getPlatformColor = () => {
    // All platforms now use black background
    return 'bg-black text-white';
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-4 right-4 z-50 w-[560px] p-2"
    >
      <div className="bg-[#EFEFEF] rounded-xl shadow-2xl overflow-hidden">
        {/* Header - Only visible when expanded */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div 
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#E5E5E5] transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <div className="flex items-center gap-3">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    className="text-gray-700 hover:text-black transition-colors"
                  >
                    <ChevronDown size={24} />
                  </button>
                  
                  <Upload size={20} className="text-gray-700" />
                  
                  <h3 className="text-black font-medium text-base">Uploads</h3>
                </div>

                {/* Only show X button if no active uploads */}
                {activeCount === 0 && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose?.();
                    }}
                    className="text-gray-700 hover:text-black transition-colors"
                  >
                    <X size={24} />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expandable content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Tabs */}
              <div className="flex items-center gap-2 px-4 py-3">
                {(['all', 'completed', 'skipped', 'failed'] as TabType[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all capitalize ${
                      activeTab === tab
                        ? 'bg-black text-white'
                        : 'text-gray-600 hover:text-black hover:bg-gray-200'
                    }`}
                  >
                    {tab === 'all' ? 'All uploads' : tab}
                  </button>
                ))}
              </div>

              {import.meta.env.DEV && (
                <div className="flex items-center gap-2 px-4 pb-3">
                  <span className="text-xs text-gray-500">UI testing:</span>
                  <button
                    type="button"
                    onClick={onSimulateFailure}
                    className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium hover:bg-red-200"
                  >
                    Simulate failure
                  </button>
                  <button
                    type="button"
                    onClick={onSimulateCancellation}
                    className="px-2 py-1 rounded bg-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-300"
                  >
                    Simulate cancellation
                  </button>
                </div>
              )}

              {/* Upload list */}
              <div
                className="upload-queue-scrollbar relative z-10 box-border h-[min(420px,calc(100vh-280px))] max-h-[min(420px,calc(100vh-280px))] overflow-y-auto overscroll-y-contain px-4"
                onWheel={(event) => event.stopPropagation()}
                tabIndex={0}
              >
                {filteredUploads.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    No {activeTab === 'all' ? '' : activeTab} uploads
                  </div>
                ) : (
                  <div className="pb-2">
                    {filteredUploads.map((upload) => (
                      <div key={upload.id} className="py-3 border-b border-gray-300 last:border-b-0">
                        {/* Upload item */}
                        <div className="flex items-start gap-3">
                          {/* Status icon - Fixed width container */}
                          <div className="flex-shrink-0 pt-0.5 w-10 flex items-center justify-center">
                            {upload.status === 'completed' && (
                              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {upload.status === 'failed' && (
                              <div className="w-5 h-5 flex items-center justify-center">
                                <span className="text-black text-lg font-bold">!</span>
                              </div>
                            )}
                            {upload.status === 'skipped' && (
                              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            {upload.status === 'uploading' && (
                              <div className="text-gray-500 text-sm font-medium">
                                {upload.progress}%
                              </div>
                            )}
                            {upload.status === 'queued' && (
                              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                              </svg>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {/* Title */}
                            <div className="text-black text-sm font-medium mb-1.5">
                              {upload.title}
                            </div>

                            {/* Platform badges and status */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* File type badge */}
                              <span className="px-2 py-0.5 bg-gray-300 text-black rounded text-xs font-medium">
                                MP3
                              </span>

                              {/* Platform badges */}
                              {upload.platforms.map((platform) => {
                                const isCancelled = upload.cancelledPlatforms?.includes(platform);
                                return (
                                  <div
                                    key={platform}
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      isCancelled 
                                        ? 'bg-gray-300 text-gray-500' 
                                        : getPlatformColor()
                                    }`}
                                  >
                                    {platform}
                                  </div>
                                );
                              })}

                              {/* Status text */}
                              {upload.status === 'completed' && (
                                <span className="text-gray-500 text-xs">
                                  {getCompletionLabel(upload)}
                                </span>
                              )}
                              {upload.status === 'failed' && (
                                <span className="text-gray-500 text-xs">
                                  Upload failed
                                </span>
                              )}
                              {upload.status === 'uploading' && upload.timeLeft && (
                                <span className="text-gray-500 text-xs">
                                  {upload.timeLeft}
                                </span>
                              )}
                              {upload.status === 'uploading' && upload.estimatedTime && (
                                <span className="text-gray-500 text-xs">
                                  {upload.estimatedTime} left
                                </span>
                              )}
                              {upload.status === 'queued' && (
                                <span className="text-gray-500 text-xs">
                                  Added to Queue 2/3
                                </span>
                              )}
                              {upload.status === 'skipped' && upload.cancelledPlatforms && (
                                <span className="text-gray-500 text-xs flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Cancelled
                                </span>
                              )}
                            </div>

                            {/* Progress bar (for uploading status) */}
                            {upload.status === 'uploading' && upload.progress !== undefined && (
                              <div className="mt-2">
                                <div className="w-full h-1.5 bg-gray-300 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${upload.progress}%` }}
                                    transition={{ duration: 0.3 }}
                                    className="h-full bg-green-500 rounded-full"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action button */}
                          <div className="flex-shrink-0">
                            {upload.status === 'failed' && (
                              <button
                                onClick={() => onRetry?.(upload.id)}
                                className="px-3 py-1 bg-black text-white rounded-md text-xs font-medium hover:bg-gray-800 transition-colors"
                              >
                                Retry
                              </button>
                            )}
                            {upload.status === 'completed' && (
                              <a
                                href="/your-videos"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = '/your-videos';
                                }}
                                className="px-3 py-1 bg-black text-white rounded-md text-xs font-medium hover:bg-gray-800 transition-colors inline-block cursor-pointer"
                              >
                                All Videos
                              </a>
                            )}
                            {upload.status === 'skipped' && (
                              <button
                                onClick={() => onRetry?.(upload.id)}
                                className="px-3 py-1 bg-black text-white rounded-md text-xs font-medium hover:bg-gray-800 transition-colors"
                              >
                                Retry
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer - Progress bar - ALWAYS VISIBLE */}
        <div className="p-2">
          <div className="relative h-[60px] bg-black rounded-xl overflow-hidden">
            {/* Progress fill follows the most recent upload shown in the footer. */}
            <motion.div 
              className={`absolute inset-0 rounded-r-lg ${
                latestUpload?.status === 'failed'
                  ? 'bg-red-700'
                  : latestUpload?.status === 'skipped'
                    ? 'bg-gray-600'
                    : 'bg-green-700'
              }`}
              initial={{ width: 0 }}
              animate={{ 
                width: `${latestUpload?.status === 'uploading' ? latestUpload.progress || 0 : latestUpload ? 100 : 0}%` 
              }}
              transition={{ duration: 0.3 }}
            />
            
            {/* Content on top of progress bar */}
            <div className="relative z-10 h-full flex items-center justify-between px-4">
              <div className="flex items-center gap-3 text-white">
                {/* Upload icon - bigger */}
                <Upload size={28} strokeWidth={2.5} />
                
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">
                    {latestUpload?.status === 'failed'
                      ? 'Upload failed'
                      : latestUpload?.status === 'skipped'
                        ? 'Upload cancelled'
                        : latestUpload?.status === 'completed'
                          ? 'Upload complete'
                          : latestUpload?.status === 'queued'
                            ? 'Upload queued'
                            : `Uploading ${activeCount} of ${totalCount} item${totalCount !== 1 ? 's' : ''}`}
                  </span>
                  <span className="opacity-50">•</span>
                  <span className="px-2 py-0.5 bg-white/20 rounded text-xs font-medium">
                    MP3
                  </span>
                  {latestUpload?.status === 'uploading' && latestUpload.timeLeft && (
                    <span className="text-xs">
                      {latestUpload.timeLeft}
                    </span>
                  )}
                  {latestUpload?.status === 'uploading' && latestUpload.estimatedTime && (
                    <span className="text-xs">
                      {latestUpload.estimatedTime} left
                    </span>
                  )}
                  {latestUpload?.status === 'failed' && (
                    <span className="text-xs">{latestUpload.timeLeft}</span>
                  )}
                  {latestUpload?.status === 'skipped' && (
                    <span className="text-xs">{latestUpload.timeLeft || 'Cancelled by user'}</span>
                  )}
                  {latestUpload?.status === 'completed' && (
                    <span className="text-xs">{getCompletionLabel(latestUpload)}</span>
                  )}
                </div>
              </div>

              {/* Right side - Always show buttons in same position */}
              <div className="flex items-center gap-3">
                {/* Show upload-another button when expanded */}
                {isExpanded && (
                  <button
                    onClick={onOpenUploadModal || onAdd}
                    className="px-4 py-1.5 bg-white text-black rounded-md text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                  >
                    Upload Another
                    <ChevronDown size={16} />
                  </button>
                )}
                
                {/* Show ChevronUp when collapsed */}
                {!isExpanded && (
                  <button 
                    onClick={() => setIsExpanded(true)}
                    className="text-white hover:opacity-80 transition-opacity"
                  >
                    <ChevronUp size={24} />
                  </button>
                )}
                
                {/* Always show X button when actively uploading */}
                {activeUpload && latestUpload?.status === 'uploading' && (
                  <button 
                    onClick={onClose}
                    className="text-white hover:opacity-80 transition-opacity"
                  >
                    <X size={24} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}